// Sourcing events API helper (Lane 1322). Every change sends a fresh
// Idempotency-Key and the revision the screen loaded; the server is the only
// source of what changed, so screens reload from it after each action.
import { useMemo, useRef } from 'react';
import { API_BASE } from '../../config';
import { useAuth } from '../../AuthContext';
import { requestChatTurn } from '../../utils/chatProgressStream';
import { formatUnitPrice } from '../sourcing-portal/portalView';
import { newKey } from '../../utils/clientKey';

// Moved to utils/clientKey (Lane 1355): the shared workspace chat uses it too.
export { newKey };

export class SourcingError extends Error {
  constructor(status, detail) {
    const message = (detail && typeof detail === 'object' && detail.message)
      || (typeof detail === 'string' ? detail : 'Something went wrong. Try again.');
    super(message);
    this.status = status;
    this.code = detail && typeof detail === 'object' ? detail.code : undefined;
    this.detail = detail;
  }
}

async function parse(response) {
  let body = null;
  try { body = await response.json(); } catch (e) { body = null; }
  if (!response.ok) throw new SourcingError(response.status, body && body.detail);
  return body;
}

export function makeSourcingApi(authFetch, authHeader = () => ({})) {
  const base = `${API_BASE}/api/sourcing`;
  const json = (method, path, body, extraHeaders = {}) => authFetch(`${base}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...extraHeaders },
    body: body === undefined ? undefined : JSON.stringify(body),
  }).then(parse);
  const get = (path, params) => {
    const qs = params ? `?${new URLSearchParams(Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')).toString()}` : '';
    return authFetch(`${base}${path}${qs}`).then(parse);
  };
  return {
    listEvents: () => get('/events'),
    filterOptions: () => get('/filter-options'),
    searchSuppliers: (q) => get('/supplier-search', { q, limit: 20 }),
    previewScope: (filters) => json('POST', '/scope-preview', { filters }),
    createEvent: (name, filters) => json('POST', '/events', { name, filters },
      { 'Idempotency-Key': newKey() }),
    event: (id) => get(`/events/${id}`),
    // Lane 1433: delete a draft or cancelled event (a published one is hidden from the
    // buyer instead; the server decides). A repeat answers the first outcome.
    deleteEvent: (id, revision) => authFetch(
      `${base}/events/${id}?expected_revision=${encodeURIComponent(revision)}`,
      { method: 'DELETE' }).then(parse),
    // The redesigned workspace (Lane 1344, UI redesign slice 1): the Overview and
    // the filtered tables (parts, suppliers, offers, part-candidates, supplier-candidates).
    overview: (id) => get(`/events/${id}/overview`),
    table: (id, name, params) => get(`/events/${id}/tables/${name}`, params),
    importLines: (id, rows, revision) => json('POST', `/events/${id}/lines/import`,
      { expected_revision: revision, rows }, { 'Idempotency-Key': newKey() }),
    lines: (id, params) => get(`/events/${id}/lines`, params),
    previewLines: (id, filters) => json('POST', `/events/${id}/lines/preview`, { filters }),
    suppliers: (id) => get(`/events/${id}/suppliers`),
    qualifications: (id, params) => get(`/events/${id}/qualifications`, params),
    notes: (id) => get(`/events/${id}/notes`),
    receipts: (id, params) => get(`/events/${id}/receipts`, params),
    // An action: method + path under the event, the input, and the revision.
    // `key` is optional: a chat proposal passes its own id, so a second click
    // (or a reload) can never apply the same proposal twice.
    act: (id, method, path, input, revision, key) => json(method, `/events/${id}${path}`,
      { expected_revision: revision, ...input }, { 'Idempotency-Key': key || newKey() }),
    actQuery: (id, method, path, revision, key) => authFetch(
      `${base}/events/${id}${path}${path.includes('?') ? '&' : '?'}expected_revision=${revision}`,
      { method, headers: { 'Idempotency-Key': key || newKey() } }).then(parse),
    // Chat inside the event (slice D). The turn streams, with the plain POST
    // as the fallback; the same client_turn_id makes a retry a replay.
    chat: (id) => get(`/events/${id}/chat`),
    newConversation: (id) => json('POST', `/events/${id}/chat/conversations`),
    // Lane 1368: a dismissed card stays dismissed after a reload (chat control on only).
    dismissCard: (id, proposalId) => json('POST',
      `/events/${id}/chat/cards/${encodeURIComponent(proposalId)}/dismiss`),
    chatTurn: (id, body, onEvent) => requestChatTurn({
      streamUrl: `${base}/events/${id}/chat/turns/stream`,
      fallbackUrl: `${base}/events/${id}/chat/turns`,
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body,
      onEvent,
    }),
    // Publish and invitations (Lane 1331, Phase 2 slice 2A). Raw invitation
    // links come back only while email is off, once, in `links`.
    publishReview: (id, params) => get(`/events/${id}/publish-review`, params),
    publish: (id, body, revision) => json('POST', `/events/${id}/publish`,
      { expected_revision: revision, ...body }, { 'Idempotency-Key': newKey() }),
    // Lane 1438: save what the buyer typed in Review and publish (no revision: the last
    // save wins). `key`: the same key for a retry of the same save.
    savePublishDraft: (id, body, key) => json('PATCH', `/events/${id}/publish-draft`, body,
      { 'Idempotency-Key': key || newKey() }),
    invitations: (id) => get(`/events/${id}/invitations`),
    // RFP invitations before launch (Lane 1440; the server answers 404 while
    // SOURCING_INVITATIONS_ENABLED is off). The letter and the marks need no revision
    // (the last save wins); the letter carries its own base_revision.
    invitationLetter: (id) => get(`/events/${id}/invitation-letter`),
    saveInvitationLetter: (id, body, key) => json('PUT', `/events/${id}/invitation-letter`, body,
      { 'Idempotency-Key': key || newKey() }),
    markInvitation: (id, body) => json('POST', `/events/${id}/invitation-status`, body,
      { 'Idempotency-Key': newKey() }),
    setLaunchDate: (id, launchDate, revision) => json('POST', `/events/${id}/launch-date`,
      { expected_revision: revision, launch_date: launchDate || null }, { 'Idempotency-Key': newKey() }),
    // Lane 1440: the supplier bid guide and the invitation email as publish sends it.
    bidGuide: (id) => authFetch(`${base}/events/${id}/bid-guide.pdf`).then(async (r) => {
      if (!r.ok) await parse(r);
      return r.blob();
    }),
    invitationEmailPreview: (id, params) => get(`/events/${id}/invitation-email-preview`, params),
    downloadInvitationLetters: (id, supplierId) => authFetch(
      `${base}/events/${id}/invitation-letter.docx${supplierId ? `?supplier_id=${supplierId}` : ''}`)
      .then(async (r) => {
        if (!r.ok) await parse(r);
        return r.blob();
      }),
    // key: a chat card's proposal id, so a prepared invitation is sent at most once.
    invitationAction: (id, action, input, revision, key) => json('POST',
      `/events/${id}/invitations/${action}`, { expected_revision: revision, ...input },
      { 'Idempotency-Key': key || newKey() }),
    // The buyer's view of the offers (Lane 1331, Phase 2 slice 2C). The
    // comparison and feedback drafts are records: they need no revision.
    offers: (id, params) => get(`/events/${id}/offers`, params),
    responses: (id) => get(`/events/${id}/responses`),
    buildComparison: (id) => json('POST', `/events/${id}/comparisons`, {},
      { 'Idempotency-Key': newKey() }),
    latestComparison: (id) => get(`/events/${id}/comparisons/latest`),
    draftFeedback: (id, input) => json('POST', `/events/${id}/disclosures/draft`, input,
      { 'Idempotency-Key': newKey() }),
    disclosures: (id) => get(`/events/${id}/disclosures`),
    // Rounds and feedback (Lane 1331, Phase 2 slice 2D). Round actions carry
    // the revision and the offers digest from their review; feedback drafts
    // are records (nothing is sent), so they need no revision.
    roundReview: (id) => get(`/events/${id}/round-review`),
    roundAction: (id, action, input, revision) => json('POST', `/events/${id}/rounds/${action}`,
      { expected_revision: revision, ...input }, { 'Idempotency-Key': newKey() }),
    feedbackAction: (id, action, input) => json('POST', `/events/${id}/disclosures/${action}`,
      input, { 'Idempotency-Key': newKey() }),
    // Changes during a round (Lane 1331, Phase 2 slice 2E). The review is a
    // read; with the buyer's own picks it is asked again (still saves nothing).
    amendmentReview: (id, picks) => (picks
      ? json('POST', `/events/${id}/amendment-review`, {
        policies: Object.entries(picks).map(([change_id, policy]) => ({ change_id, policy })) })
      : get(`/events/${id}/amendment-review`)),
    amendmentAction: (id, action, input, revision) => json('POST', `/events/${id}/amendments/${action}`,
      { expected_revision: revision, ...input }, { 'Idempotency-Key': newKey() }),
    // Buyer documents (Lane 1331, Phase 2 slice 2E-2). After publish they
    // change only on a change in progress (version 'amendment').
    documents: (id, version) => get(`/events/${id}/documents`, { version }),
    // options (Lane 1440, the Documents dialog): {display_name, audience, supplier_ids,
    // informational}; sent only by the new Documents tab.
    uploadDocument: (id, file, revision, flags, options) => {
      const form = new FormData();
      form.append('file', file);
      if (options) form.append('options', JSON.stringify(options));
      const qs = new URLSearchParams({ expected_revision: revision,
        supplier_visible: Boolean(flags.supplier_visible), informational: Boolean(flags.informational) });
      return authFetch(`${base}/events/${id}/documents?${qs}`,
        { method: 'POST', headers: { 'Idempotency-Key': newKey() }, body: form }).then(parse);
    },
    replaceDocument: (id, docId, file, revision, options) => {
      const form = new FormData();
      form.append('file', file);
      if (options && Object.keys(options).length) form.append('options', JSON.stringify(options));
      return authFetch(`${base}/events/${id}/documents/${docId}/replace?expected_revision=${revision}`,
        { method: 'POST', headers: { 'Idempotency-Key': newKey() }, body: form }).then(parse);
    },
    documentAction: (id, docId, action, input, revision) => json('POST',
      `/events/${id}/documents/${docId}/${action}`, { expected_revision: revision, ...input },
      { 'Idempotency-Key': newKey() }),
    // A supplier's emailed bid file, entered by the buyer (Lane 1394): download the
    // supplier's file, review an upload (writes nothing), enter it (the server reads the
    // file again; file_digest is the review's).
    downloadSupplierFile: (id, supplierId) => authFetch(
      `${base}/events/${id}/suppliers/${supplierId}/bid-file`).then(async (r) => {
      if (!r.ok) await parse(r);
      return r.blob();
    }),
    reviewSupplierFile: (id, supplierId, file) => {
      const form = new FormData();
      form.append('file', file);
      return authFetch(`${base}/events/${id}/suppliers/${supplierId}/bid-file/review`,
        { method: 'POST', body: form }).then(parse);
    },
    enterSupplierFile: (id, supplierId, file, fileDigest, key) => {
      const form = new FormData();
      form.append('file', file);
      form.append('file_digest', fileDigest);
      return authFetch(`${base}/events/${id}/suppliers/${supplierId}/bid-file/enter`,
        { method: 'POST', headers: { 'Idempotency-Key': key || newKey() }, body: form }).then(parse);
    },
    downloadDocument: (id, docId, version) => authFetch(
      `${base}/events/${id}/documents/${docId}?version=${version}`).then(async (r) => {
      if (!r.ok) await parse(r);
      return r.blob();
    }),
    // Rules, scenarios and calculations (Lane 1344, Phase 3). Rules and scenarios
    // carry their own version checks, not the event revision.
    rules: (id) => get(`/events/${id}/rules`),
    // key: a chat card's proposal id, so a prepared change applies at most once.
    ruleAction: (id, action, input, key) => json('POST', `/events/${id}/rules/${action}`, input,
      { 'Idempotency-Key': key || newKey() }),
    scenarios: (id) => get(`/events/${id}/scenarios`),
    scenario: (id, sid) => get(`/events/${id}/scenarios/${sid}`),
    scenarioAction: (id, action, input, key) => json('POST', `/events/${id}/scenarios/${action}`, input,
      { 'Idempotency-Key': key || newKey() }),
    scenarioResult: (id, rid, params) => get(`/events/${id}/scenario-results/${rid}`, params),
    // A result's Parts list with column filters (slice 2b: hand moves pick lines here).
    resultLines: (id, rid, params) => get(`/events/${id}/scenario-results/${rid}/lines`, params),
    calculation: (id, jid) => get(`/events/${id}/calculations/${jid}`),
    compareScenarios: (id, a, b) => get(`/events/${id}/scenario-compare`, { a, b }),
    // Workspace redesign slice 2: the Evaluation rules tab, the side-by-side comparison
    // (up to six scenarios) and the buyer's yes to a chat proposal (its id is the key,
    // so a double click creates nothing twice).
    evaluationRules: (id) => get(`/events/${id}/evaluation-rules`),
    scenarioComparison: (id, ids) => get(`/events/${id}/scenario-comparison`,
      ids && ids.length ? { ids: ids.join(',') } : undefined),
    confirmProposal: (id, proposalId, conversationId) => json('POST', `/events/${id}/scenarios/confirm-proposal`,
      { proposal_id: proposalId, conversation_id: conversationId }, { 'Idempotency-Key': proposalId }),
    supplierNotes: (id) => get(`/events/${id}/supplier-notes`),
    reviewNote: (id, input) => json('POST', `/events/${id}/supplier-notes/review`, input,
      { 'Idempotency-Key': newKey() }),
    importFile: (id, file, revision) => {
      const form = new FormData();
      form.append('file', file);
      return authFetch(`${base}/events/${id}/lines/import-file?expected_revision=${revision}`,
        { method: 'POST', headers: { 'Idempotency-Key': newKey() }, body: form }).then(parse);
    },
    // The Negotiation tab (Lane 1354). Targets are records: no revision.
    negotiation: (id, supplierId) => get(`/events/${id}/negotiation`,
      supplierId ? { supplier_id: supplierId } : undefined),
    negotiationAction: (id, path, input) => json('POST', `/events/${id}/negotiation/${path}`, input,
      { 'Idempotency-Key': newKey() }),
    negotiationIntelligence: (id, supplierId) => get(`/events/${id}/negotiation/intelligence`,
      { supplier_id: supplierId }),
    // Supplier history on the award screens (Lane 1395 part 2): the suppliers, one
    // supplier's mark, and the pop-up.
    supplierHistoryList: (id) => get(`/events/${id}/supplier-history`),
    supplierHistoryMarks: (id, supplierId) => get(`/events/${id}/supplier-history/${supplierId}/marks`),
    supplierHistory: (id, supplierId) => get(`/events/${id}/supplier-history/${supplierId}`),
    // The award (Lane 1395): the review of one result, the award itself (with the
    // revision and the digest the buyer reviewed), the saved award, its download and a
    // resend of notices that failed.
    awardReview: (id, resultId) => get(`/events/${id}/award-review`, { result_id: resultId }),
    award: (id, revision, input, key) => json('POST', `/events/${id}/award`,
      { expected_revision: revision, ...input }, { 'Idempotency-Key': key || newKey() }),
    awardView: (id) => get(`/events/${id}/award`),
    downloadAward: (id) => authFetch(`${base}/events/${id}/award/download`).then(async (r) => {
      if (!r.ok) await parse(r);
      return r.blob();
    }),
    resendAwardNotices: (id) => json('POST', `/events/${id}/award/resend-notices`, {},
      { 'Idempotency-Key': newKey() }),
    // Reopen the standing award (Lane 1395 QA): no notice; the revision and the award
    // the buyer saw are required.
    reopenAward: (id, revision, awardId, reason, key) => json('POST', `/events/${id}/award/reopen`,
      { expected_revision: revision, award_id: awardId, reason: reason || null },
      { 'Idempotency-Key': key || newKey() }),
    // The buyer changes suppliers' prices on the Offers grid (Lane 1395 QA). One key per
    // save attempt, so a click whose reply was lost replays, never saves twice.
    editOffers: (id, body, key) => json('POST', `/events/${id}/offers/edit`, body,
      { 'Idempotency-Key': key || newKey() }),
    // A negotiation deck's build status: the same read Meeting Prep polls (Lane 1362),
    // so the tab and Meeting Prep always show the one build.
    templateDeck: (deckVersionId) => authFetch(`${API_BASE}/api/template-decks/${deckVersionId}`).then(parse),
  };
}

// A sentence that ends with a name: one full stop, never "Ltd.." (Lane 1362).
export function endSentence(text) {
  const t = String(text).trimEnd();
  return /[.!?]$/.test(t) ? t : `${t}.`;
}

// What every screen says when its change was refused because the event moved
// on (stale_revision). The screen reloads; the message stays so the change
// never disappears silently.
export const STALE_MESSAGE = 'This event changed while you were editing. Your change was not saved. Check it and try again.';

// The supplier's join link for a raw invitation token. The token rides in the
// URL fragment, which browsers never send to a server.
export function portalJoinUrl(token) {
  return `${window.location.origin}/sourcing-portal/join#t=${token}`;
}

// Plain words for why a line cannot be evaluated yet.
export const INCOMPLETE_TEXT = {
  no_history_quantity: 'No purchases here in the last 12 months. Enter a volume.',
  non_positive_quantity: 'Returns cancel out the purchases. Enter a volume.',
  no_baseline: 'Never bought at this site, so there is no last price.',
  incumbent_unresolved: 'Pick the current supplier.',
  quantity_not_whole_steps: 'The volume is not a whole number of buying steps.',
  mixed_units: 'Bought in more than one unit of measure. Enter a volume.',
};

export const IMPORT_REASON_TEXT = {
  unknown_part: 'Not a part in your catalog',
  unknown_site: 'Not a site you buy at',
  site_required: 'Add a site for this part',
  duplicate_row: 'Listed twice',
};

export function formatMoney(value, digits = 0) {
  if (value === null || value === undefined) return 'Not available';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits,
  }).format(value);
}

export function formatPrice(value) {
  if (value === null || value === undefined) return 'Not available';
  const digits = Math.abs(value) < 1 ? 4 : 2;
  return formatMoney(value, digits);
}

// An amount the server sent as a string, in the event's currency. The browser
// only formats it; it never adds or subtracts figures.
export function formatAmount(value, currency, digits = 2) {
  if (value === null || value === undefined || value === '') return 'Not available';
  const n = Number(value);
  if (!Number.isFinite(n)) return 'Not available';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: currency || 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits,
    }).format(n);
  } catch (e) {
    return `${n.toFixed(digits)} ${currency || ''}`.trim();
  }
}

// A unit price at the server's precision (2 to 4 decimals). One function for
// buyer and supplier screens; it lives with the supplier display pieces.
export { formatUnitPrice };

// A price in its own currency and unit (a today's price that cannot be
// compared with the event's offers).
export function formatOwnPrice(value, currency, unit) {
  const price = formatUnitPrice(value, currency);
  return unit ? `${price} per ${unit}` : price;
}

export function formatUnits(value) {
  if (value === null || value === undefined) return 'Not available';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
}

export function formatDate(iso) {
  if (!iso) return 'Not available';
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return 'Not available';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatWindow(window) {
  if (!window) return '';
  const end = new Date(`${window.end}T00:00:00`);
  end.setDate(end.getDate() - 1);
  const f = (d) => d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  return `${f(new Date(`${window.start}T00:00:00`))} to ${f(end)}`;
}

// A pasted part list: one part per line; optional site and volume separated by
// tabs (a spreadsheet paste) or commas ("NG-123, Lancaster Plant, 500").
export function parsePastedParts(text) {
  return text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean).map((line) => {
    const cells = (line.includes('\t') ? line.split('\t') : line.split(',')).map((c) => c.trim());
    const row = { material_number: cells[0] };
    if (cells[1]) row.site = cells[1];
    if (cells[2]) {
      const qty = Number(cells[2].replace(/,/g, ''));
      if (Number.isFinite(qty) && qty > 0) row.quantity = qty;
    }
    return row;
  }).filter((r) => r.material_number);
}

// One stable API object per page, even when the auth provider re-renders.
export function useSourcingApi() {
  const { authFetch, getAuthHeader } = useAuth();
  const ref = useRef({ authFetch, getAuthHeader });
  ref.current = { authFetch, getAuthHeader };
  return useMemo(() => makeSourcingApi(
    (...args) => ref.current.authFetch(...args),
    () => ref.current.getAuthHeader(),
  ), []);
}
